/**
 * Behavior Resolver
 * 
 * Maps NPC behavior configuration (from map annotations) to behavior instances.
 * Registry pattern with factory functions.
 */

import type { NPCBehavior, NPCBehaviorConfig } from './NPCBehavior';
import { createPassivePatrol } from './passivePatrol';
import { createAggressivePatrol } from './aggressivePatrol';
import { createPassiveWander } from './passiveWander';
import { createAggressiveWander } from './aggressiveWander';

/**
 * Factory function type for creating behaviors
 */
type BehaviorFactory = (config: NPCBehaviorConfig) => NPCBehavior;

/**
 * Registry of behavior factories by ID
 */
const behaviorFactories = new Map<string, BehaviorFactory>([
  ['passive_patrol', createPassivePatrol],
  ['aggressive_patrol', createAggressivePatrol],
  ['passive_wander', createPassiveWander],
  ['aggressive_wander', createAggressiveWander],
]);

/**
 * Register a custom behavior factory
 */
export const registerBehavior = (id: string, factory: BehaviorFactory): void => {
  behaviorFactories.set(id, factory);
};

/**
 * Get all registered behavior IDs
 */
export const getRegisteredBehaviors = (): string[] => {
  return Array.from(behaviorFactories.keys());
};

/**
 * Resolve a behavior from configuration.
 * 
 * Priority:
 * 1. Direct behaviorId if specified
 * 2. Compose from movement + combat presets
 * 3. Default to passive_patrol
 * 
 * @param config - Configuration from map annotations
 * @returns NPCBehavior instance
 */
export const resolveBehavior = (config: NPCBehaviorConfig): NPCBehavior => {
  // 1. Direct behavior ID takes precedence
  if (config.behaviorId) {
    const factory = behaviorFactories.get(config.behaviorId);
    if (factory) {
      return factory(config);
    }
    console.warn(`[BehaviorResolver] Unknown behavior ID: ${config.behaviorId}, falling back to passive_patrol`);
  }
  
  // 2. Compose from movement + combat
  const movement = config.movement ?? 'patrol';
  const combat = config.combat ?? 'passive';
  
  // Map common combinations to behaviors
  const composedId = `${combat}_${movement}`;
  const factory = behaviorFactories.get(composedId);
  
  if (factory) {
    return factory(config);
  }
  
  // 3. Fallback based on combat type
  if (combat === 'aggressive' || combat === 'territorial') {
    return createAggressivePatrol(config);
  }
  
  // Default: passive patrol
  return createPassivePatrol(config);
};

/**
 * Parse behavior config from mesh metadata (hm_* annotations)
 * 
 * @param extras - gltf.extras from mesh metadata
 * @returns NPCBehaviorConfig
 */
export const parseBehaviorConfig = (extras: Record<string, unknown> | undefined): NPCBehaviorConfig => {
  if (!extras) {
    return {};
  }
  
  return {
    behaviorId: extras.hm_npc_behavior_id as string | undefined,
    movement: extras.hm_npc_movement as NPCBehaviorConfig['movement'],
    combat: extras.hm_npc_combat as NPCBehaviorConfig['combat'],
    patrolLeft: extras.hm_npc_patrol_left as number | undefined,
    patrolRight: extras.hm_npc_patrol_right as number | undefined,
    attackRange: extras.hm_npc_attack_range as number | undefined,
    detectionRange: extras.hm_npc_detection_range as number | undefined,
    attackCooldown: extras.hm_npc_attack_cooldown as number | undefined,
    spawnT: extras.hm_npc_spawn_t as number | undefined,
  };
};
